import {
  SimulationEnvironment,
  SimulationEnvironmentSource,
  SimulationSky,
  SimulationThreatApproach,
  SimulationThreatTrajectory,
  SimulationThreatType,
  SimulationTimeOfDay,
  type SimulationScenario,
  type SimulationThreat,
} from '@ddp/shared';

import type { MessageKey } from '../../i18n/index.js';

/**
 * Templates de scénario (ADR-0021/0023) : des points de départ prêts à ajuster, côté client. Les
 * cibles au sol reprennent des labels usuels ; les menaces aériennes portent un type figé (le nom de
 * classe étant repris du référentiel, cf. `label`). Chaque scénario est complet et valide au regard
 * du contrat partagé `simulationScenarioSchema`.
 */
export interface SimulationTemplate {
  id: string;
  nameKey: MessageKey;
  scenario: SimulationScenario;
}

const baseOutputs = { labeledImages: true, video: false } as const;
// Champs communs : décor générique par défaut, sans média de référence (incrément C).
const presetEnv = {
  environmentSource: SimulationEnvironmentSource.PRESET,
  referenceMedia: [],
} satisfies Pick<SimulationScenario, 'environmentSource' | 'referenceMedia'>;

export const SIMULATION_TEMPLATES: SimulationTemplate[] = [
  {
    id: 'field-search',
    nameKey: 'sim.template.fieldSearch',
    scenario: {
      environment: SimulationEnvironment.FIELD,
      ...presetEnv,
      timeOfDay: SimulationTimeOfDay.DAY,
      sky: SimulationSky.PARTLY_CLOUDY,
      targets: [{ label: 'personne', density: 3 }],
      threats: [],
      altitudeM: 45,
      gimbalPitchDeg: 60,
      outputs: { ...baseOutputs },
      imageCount: 200,
      videoDurationSeconds: 20,
      seed: null,
    },
  },
  {
    id: 'forest-inspection',
    nameKey: 'sim.template.forestInspection',
    scenario: {
      environment: SimulationEnvironment.FOREST,
      ...presetEnv,
      timeOfDay: SimulationTimeOfDay.DAWN,
      sky: SimulationSky.CLEAR,
      targets: [{ label: 'véhicule', density: 1 }],
      threats: [],
      altitudeM: 80,
      gimbalPitchDeg: 35,
      outputs: { ...baseOutputs },
      imageCount: 200,
      videoDurationSeconds: 20,
      seed: null,
    },
  },
  {
    id: 'periurban-night',
    nameKey: 'sim.template.periurbanNight',
    scenario: {
      environment: SimulationEnvironment.PERIURBAN,
      ...presetEnv,
      timeOfDay: SimulationTimeOfDay.NIGHT,
      sky: SimulationSky.CLEAR,
      targets: [{ label: 'personne', density: 2 }],
      threats: [],
      altitudeM: 60,
      gimbalPitchDeg: 45,
      outputs: { ...baseOutputs },
      imageCount: 240,
      videoDurationSeconds: 20,
      seed: null,
    },
  },
  {
    // Défense anti-drone : pas de cible au sol, la menace EST l'objet à détecter (ADR-0023).
    id: 'counter-drone',
    nameKey: 'sim.template.counterDrone',
    scenario: {
      environment: SimulationEnvironment.PERIURBAN,
      ...presetEnv,
      timeOfDay: SimulationTimeOfDay.DAY,
      sky: SimulationSky.CLEAR,
      targets: [],
      threats: [
        {
          type: SimulationThreatType.FPV,
          label: null,
          sizeInches: 10,
          count: 1,
          speedMinMs: 25,
          speedMaxMs: 40,
          approach: SimulationThreatApproach.FRONTAL,
          trajectory: SimulationThreatTrajectory.DIVE,
          dispersionDeg: null,
        },
      ],
      altitudeM: 80,
      gimbalPitchDeg: 20,
      outputs: { labeledImages: true, video: true },
      imageCount: 240,
      videoDurationSeconds: 20,
      seed: null,
    },
  },
];

/** Menace vierge : point de départ neutre quand on ajoute une menace à la main. */
export const BLANK_THREAT: SimulationThreat = {
  type: SimulationThreatType.FPV,
  label: null,
  sizeInches: null,
  count: 1,
  speedMinMs: 10,
  speedMaxMs: 20,
  approach: SimulationThreatApproach.FRONTAL,
  trajectory: SimulationThreatTrajectory.PASS,
  dispersionDeg: null,
};

/** Presets de menace : des menaces prêtes à ajuster, ajoutées d'un clic. */
export interface ThreatPreset {
  id: string;
  nameKey: MessageKey;
  threat: SimulationThreat;
}

export const THREAT_PRESETS: ThreatPreset[] = [
  {
    id: 'fpv-kamikaze',
    nameKey: 'sim.threatPreset.fpvKamikaze',
    threat: {
      type: SimulationThreatType.FPV,
      label: null,
      sizeInches: 10,
      count: 1,
      speedMinMs: 25,
      speedMaxMs: 40,
      approach: SimulationThreatApproach.FRONTAL,
      trajectory: SimulationThreatTrajectory.DIVE,
      dispersionDeg: null,
    },
  },
  {
    id: 'swarm',
    nameKey: 'sim.threatPreset.swarm',
    threat: {
      type: SimulationThreatType.QUAD,
      label: null,
      sizeInches: 7,
      count: 10,
      speedMinMs: 8,
      speedMaxMs: 18,
      approach: SimulationThreatApproach.MIXED,
      trajectory: SimulationThreatTrajectory.DISPERSE,
      dispersionDeg: 120,
    },
  },
  {
    id: 'fixed-wing',
    nameKey: 'sim.threatPreset.fixedWing',
    threat: {
      type: SimulationThreatType.FIXED_WING,
      label: null,
      sizeInches: null,
      count: 1,
      speedMinMs: 20,
      speedMaxMs: 30,
      approach: SimulationThreatApproach.LATERAL,
      trajectory: SimulationThreatTrajectory.PASS,
      dispersionDeg: null,
    },
  },
];

/** Scénario vierge : point de départ neutre quand on n'utilise aucun template. */
export const BLANK_SCENARIO: SimulationScenario = {
  environment: SimulationEnvironment.FIELD,
  environmentSource: SimulationEnvironmentSource.PRESET,
  referenceMedia: [],
  timeOfDay: SimulationTimeOfDay.DAY,
  sky: SimulationSky.CLEAR,
  targets: [],
  threats: [],
  altitudeM: 60,
  gimbalPitchDeg: 45,
  outputs: { labeledImages: true, video: false },
  imageCount: 200,
  videoDurationSeconds: 20,
  seed: null,
};
